import type { MaxPositionsLimit } from '@covered-calls/shared';

/**
 * Props for {@link TrialLimitReachedMessage}.
 *
 * The message can be seeded from the values carried on the
 * Trial_Limit_Reached_Error payload so the copy can reflect the tenant's
 * resolved limit and usage at the time of rejection.
 */
export interface TrialLimitReachedMessageProps {
  /** The tenant's resolved Effective_Limit at the time of rejection. */
  effectiveLimit: MaxPositionsLimit;
  /** The tenant's cumulative Positions_Created_Count at the time of rejection. */
  positionsCreated: number;
  /**
   * The id of the create-trade control this message relates to. When provided,
   * it is exposed via `aria-details` so assistive technologies can associate
   * the alert with the create action.
   */
  createActionId?: string;
}

/**
 * Accessible, informational limit-reached message shown when a position
 * creation is rejected with the Trial_Limit_Reached_Error.
 *
 * It is a live-region/alert associated with the create-trade action, so
 * assistive technologies announce the full message without the user having to
 * move focus (Req 9.3). The copy states that the trial position limit has been
 * reached and that moving to a paid plan removes the limit (Req 9.1). It
 * contains no pricing, checkout, or upgrade-purchase UI — plain informational
 * wording only (Req 9.2). Every colour is expressed through `var(--color-*)`
 * design tokens (Req 12.3).
 *
 * Validates: Requirements 9.1, 9.2, 9.3, 12.3
 */
export function TrialLimitReachedMessage({
  effectiveLimit,
  positionsCreated,
  createActionId,
}: TrialLimitReachedMessageProps) {
  const limitLabel =
    typeof effectiveLimit === 'number' ? String(effectiveLimit) : effectiveLimit;

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      aria-details={createActionId}
      style={{
        marginTop: '0.75rem',
        padding: '0.75rem 1rem',
        borderRadius: '6px',
        border: '1px solid var(--color-chip-warning-text)',
        backgroundColor: 'var(--color-chip-warning-bg)',
        color: 'var(--color-chip-warning-text)',
        fontSize: '0.875rem',
        lineHeight: 1.5,
      }}
    >
      <strong style={{ display: 'block', marginBottom: '0.25rem' }}>
        Trial position limit reached
      </strong>
      <span>
        You have used {positionsCreated} of your {limitLabel} trial positions and
        cannot create any more on the trial plan. Moving to a paid plan removes
        this limit.
      </span>
    </div>
  );
}
